import defaultSquare from "./logos/_default.webp?url";
import { useMemo } from "react";
import {
	type IntegrationSource,
	useGetIntegrationSourcesQuery,
} from "src/common/service/fusionHub/fusionHubApi.ts";

export type SourceSystemMetadata = {
	name: string;
	logoUrl: string;
	docsUrl: string | undefined;
	group: "erp" | "other" | "demo";
	underDevelopment?: boolean;
	hasErAdapter: boolean;
};

const DOCS_BASE = "https://docs.exopen.se/data-sources/guides";

// Filenames from docs/datasources/guides/ — adding a file there is enough to activate the link.
const KNOWN_DOC_SLUGS = new Set([
	"aaro",
	"afas",
	"caspeco",
	"exact-online",
	"finago-office",
	"fortnox",
	"kleer",
	"mediusgo",
	"microsoft-dynamics-365-business-central",
	"microsoft-dynamics-365",
	"next",
	"poweroffice",
	"procountor",
	"quickbooks-online",
	"sie",
	"skatteverket",
	"spiris",
	"tripletex",
	"unit4-erp",
	"upsales",
	"visma-control-edge",
	"visma-dinero",
	"visma-e-conomic",
	"visma-net",
	"visma-netvisor",
	"vitec-ekonomi",
	"vitec-hyra",
	"xero",
	"xledger",
]);

const toDocSlug = (displayName: string): string =>
	displayName.toLowerCase().replace(/\s+/g, "-");

const LOCAL_METADATA: Record<string, SourceSystemMetadata> = {};

// Pumpstation source enum values whose lowercased form doesn't match the
// canonical fusionhub key. Maps the legacy key to the canonical key.
const KEY_ALIASES: Record<string, string> = {
	businesscentral: "business_central",
};

const tagsToGroup = (tags: string[]): "erp" | "other" | "demo" => {
	if (tags.includes("ERP")) {
		return "erp";
	}
	if (tags.includes("DEMO")) {
		return "demo";
	}
	return "other";
};

const resolveDocsUrl = (displayName: string): string | undefined => {
	const slug = toDocSlug(displayName);
	return KNOWN_DOC_SLUGS.has(slug) ? `${DOCS_BASE}/${slug}` : undefined;
};

const apiToMetadata = (source: IntegrationSource): SourceSystemMetadata => ({
	name: source.displayName,
	logoUrl: source.logoUrl,
	docsUrl: resolveDocsUrl(source.displayName),
	group: tagsToGroup(source.tags),
	underDevelopment: source.underDevelopment,
	hasErAdapter: source.hasErAdapter,
});

const fallbackMetadata = (sourceSystem: string): SourceSystemMetadata => ({
	name: sourceSystem,
	logoUrl: defaultSquare,
	docsUrl: undefined,
	group: "other",
	hasErAdapter: false,
});

export const getSourceMetadata = (
	map: Record<string, SourceSystemMetadata>,
	sourceSystem: string | undefined,
): SourceSystemMetadata => {
	if (!sourceSystem) {
		return fallbackMetadata("");
	}
	const key = sourceSystem.toLowerCase();
	return map[key] ?? map[KEY_ALIASES[key]] ?? fallbackMetadata(sourceSystem);
};

export const useSourceSystemMetadataMap = () => {
	const { data } = useGetIntegrationSourcesQuery();
	return useMemo(() => {
		const map: Record<string, SourceSystemMetadata> = { ...LOCAL_METADATA };
		if (data) {
			for (const source of data) {
				map[source.name.toLowerCase()] = apiToMetadata(source);
			}
		}
		return map;
	}, [data]);
};

export const useSourceSystemMetadata = (
	sourceSystem: string,
): SourceSystemMetadata => {
	const map = useSourceSystemMetadataMap();
	return getSourceMetadata(map, sourceSystem);
};
